import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, ShieldOff, RotateCcw, ShieldCheck, SlidersHorizontal, KeyRound, Check, X, Plus } from "lucide-react";
import api from "@/lib/api";
import Pager from "@/components/Pager";

export default function AdminLicenses() {
  const [rows, setRows] = useState([]);
  const [rreqs, setRreqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PER = 20;
  const pages = Math.max(1, Math.ceil(rows.length / PER));
  const view = rows.slice((page - 1) * PER, page * PER);
  const [products, setProducts] = useState([]);
  const [showIssue, setShowIssue] = useState(false);
  const [issueForm, setIssueForm] = useState({ user_email: "", product_id: "", note: "", notify: true });
  const [issuing, setIssuing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/licenses");
      setRows(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  const loadReqs = async () => {
    try { const { data } = await api.get("/admin/rebind-requests"); setRreqs(data); }
    catch { /* silent */ }
  };
  useEffect(() => {
    load(); loadReqs();
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const approveReq = async (r) => {
    try { await api.post(`/admin/rebind-requests/${r.id}/approve`); toast.success("Rebind reset approved"); load(); loadReqs(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const rejectReq = async (r) => {
    const note = prompt(`Reject rebind request from ${r.user_email}? Optional reason:`, "");
    if (note === null) return;
    try { await api.post(`/admin/rebind-requests/${r.id}/reject`, { note }); toast.success("Request rejected"); loadReqs(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const revoke = async (l) => {
    if (!confirm(`Revoke license ${l.license_key}?`)) return;
    try {
      await api.post(`/admin/licenses/${l.id}/revoke`);
      toast.success("Revoked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reset = async (l) => {
    try {
      await api.post(`/admin/licenses/${l.id}/reset-rebinds`);
      toast.success("Rebinds reset");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setMax = async (l) => {
    const v = prompt(`Rebind limit for ${l.license_key} (currently ${l.rebinds_max}). Enter new max:`, l.rebinds_max);
    if (v === null) return;
    const max = parseInt(v, 10);
    if (Number.isNaN(max) || max < 0) { toast.error("Enter a valid number"); return; }
    try {
      await api.post(`/admin/licenses/${l.id}/rebinds`, { rebinds_max: max });
      toast.success("Rebind limit updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const restore = async (l) => {
    try {
      await api.post(`/admin/licenses/${l.id}/restore`);
      toast.success("License restored");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const issueLicense = async () => {
    if (!issueForm.user_email.trim() || !issueForm.product_id) { toast.error("Enter the user's email and pick a product"); return; }
    setIssuing(true);
    try {
      const { data } = await api.post("/admin/licenses/issue", issueForm);
      toast.success(`License issued: ${data.license.license_key}${data.notified ? " · user notified" : ""}`);
      setShowIssue(false);
      setIssueForm({ user_email: "", product_id: "", note: "", notify: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to issue"); }
    finally { setIssuing(false); }
  };

  return (
    <div data-testid="admin-licenses">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · Licensing</p>
          <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Licenses</h1>
          <p className="mt-2 text-sm text-emerald-100/60">{rows.length} license{rows.length === 1 ? "" : "s"} issued.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowIssue(true)} data-testid="admin-issue-license-open" className="btn-edge text-xs"><Plus size={14} /> Issue license</button>
          <button onClick={load} className="btn-edge-ghost text-xs"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {rreqs.length > 0 && (
        <div className="mb-6 card-edge overflow-hidden" data-testid="rebind-requests">
          <div className="flex items-center gap-2 border-b border-emerald-500/10 px-5 py-4">
            <KeyRound size={16} className="text-amber-300" /><p className="overline">Rebind reset requests</p>
            <span className="chip chip-amber ml-1">{rreqs.length}</span>
          </div>
          <div className="divide-y divide-emerald-500/8">
            {rreqs.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 text-sm" data-testid={`rebind-request-${r.id}`}>
                <div className="min-w-0">
                  <p className="text-white">{r.user_name || r.user_email} · <span className="font-mono-edge text-lime-300">{r.license_key}</span></p>
                  <p className="text-[11px] text-emerald-100/50">{r.product_title}{r.reason ? ` · "${r.reason}"` : ""} · {new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approveReq(r)} data-testid={`rebind-approve-${r.id}`} className="btn-edge text-xs"><Check size={13} /> Approve &amp; reset</button>
                  <button onClick={() => rejectReq(r)} data-testid={`rebind-reject-${r.id}`} className="rounded-lg border border-emerald-500/15 px-3 py-1.5 text-xs text-emerald-100/70 hover:border-amber-400/50 hover:text-amber-300"><X size={13} /> Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-edge overflow-hidden">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-emerald-500/10 text-xs uppercase tracking-widest text-emerald-100/50">
            <tr>
              <th className="px-5 py-3 font-medium">Key</th>
              <th className="px-5 py-3 font-medium">Product</th>
              <th className="px-5 py-3 font-medium">User</th>
              <th className="px-5 py-3 font-medium">Bound to</th>
              <th className="px-5 py-3 font-medium">Rebinds</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-emerald-100/50">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-emerald-100/50">No licenses yet — issued automatically on paid orders.</td></tr>}
            {view.map((l) => (
              <tr key={l.id} className="border-b border-emerald-500/8 last:border-0 hover:bg-emerald-500/[0.03]">
                <td className="px-5 py-3.5 font-mono-edge text-xs text-lime-300">{l.license_key}</td>
                <td className="px-5 py-3.5 text-white">{l.product_title}</td>
                <td className="px-5 py-3.5 text-emerald-100/70">{l.user_email}</td>
                <td className="px-5 py-3.5 font-mono-edge text-[11px] text-emerald-100/60">
                  {l.account_number ? `${l.platform} · ${l.account_number}` : "—"}
                </td>
                <td className="px-5 py-3.5 text-emerald-100/70">{l.rebinds_used}/{l.rebinds_max}</td>
                <td className="px-5 py-3.5">
                  <span className={`chip ${
                    l.status === "active" ? "chip-emerald" :
                    l.status === "revoked" ? "chip-amber" : "chip-neutral"
                  }`}>{l.status}</span>
                  {["monthly", "yearly"].includes(l.billing_period) && l.expires_at && (
                    <p className={`mt-1 text-[10px] ${l.sub_status === "expired" ? "text-amber-300" : "text-emerald-100/50"}`} data-testid={`admin-license-sub-${l.id}`}>
                      {l.sub_status === "expired" ? "Expired" : l.sub_status === "grace" ? "Grace" : "Renews"} {new Date(l.expires_at).toLocaleDateString()}
                    </p>
                  )}
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setMax(l)} data-testid={`admin-license-setmax-${l.id}`}
                      className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-lime-400/50 hover:text-white" title="Set rebind limit">
                      <SlidersHorizontal size={14} />
                    </button>
                    <button onClick={() => reset(l)} data-testid={`admin-license-reset-${l.id}`}
                      className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-lime-400/50 hover:text-white" title="Reset rebinds">
                      <RotateCcw size={14} />
                    </button>
                    {l.status !== "revoked" ? (
                      <button onClick={() => revoke(l)} data-testid={`admin-license-revoke-${l.id}`}
                        className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-red-500/40 hover:text-red-300" title="Revoke">
                        <ShieldOff size={14} />
                      </button>
                    ) : (
                      <button onClick={() => restore(l)} data-testid={`admin-license-restore-${l.id}`}
                        className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-lime-400/50 hover:text-white" title="Restore">
                        <ShieldCheck size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager page={page} pages={pages} total={rows.length} onPage={setPage} testId="admin-licenses-pager" />
      </div>

      {showIssue && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="issue-license-modal" onClick={() => setShowIssue(false)}>
          <div className="w-full max-w-md card-edge p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center gap-2"><KeyRound size={16} className="text-lime-300" /><p className="overline">Issue a license</p></div>
            <p className="mb-4 text-xs text-emerald-100/55">Give a product license to a registered user without a purchase (gift, comp, or support fix). It appears in their dashboard and binds to their MT4/MT5 account like any paid license.</p>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">User email</label>
            <input value={issueForm.user_email} onChange={(e) => setIssueForm({ ...issueForm, user_email: e.target.value })} data-testid="issue-license-email" placeholder="buyer@example.com"
              className="mb-3 w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white placeholder:text-emerald-100/30 focus:border-lime-400/60 focus:outline-none" />
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Product</label>
            <select value={issueForm.product_id} onChange={(e) => setIssueForm({ ...issueForm, product_id: e.target.value })} data-testid="issue-license-product"
              className="mb-3 w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-lime-400/60 focus:outline-none">
              <option value="">Select a product…</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Note (optional)</label>
            <input value={issueForm.note} onChange={(e) => setIssueForm({ ...issueForm, note: e.target.value })} data-testid="issue-license-note" placeholder="e.g. support replacement"
              className="mb-4 w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white placeholder:text-emerald-100/30 focus:border-lime-400/60 focus:outline-none" />
            <label className="mb-5 flex cursor-pointer items-center gap-2 text-xs text-emerald-100/70">
              <input type="checkbox" checked={issueForm.notify} onChange={(e) => setIssueForm({ ...issueForm, notify: e.target.checked })} data-testid="issue-license-notify" className="accent-lime-400" />
              Notify the user (email + in-app + Telegram if linked)
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowIssue(false)} className="btn-edge-ghost text-sm">Cancel</button>
              <button onClick={issueLicense} disabled={issuing} data-testid="issue-license-submit" className="btn-edge text-sm">{issuing ? "Issuing…" : "Issue license"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
